import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { supportedLanguages } from '../i18n';

const AppleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
  </svg>
);

const PlayIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
  </svg>
);

const BrowserIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" className={className} aria-hidden>
    <rect x="2.75" y="4.25" width="18.5" height="15.5" rx="2.75" />
    <path d="M2.75 9h18.5" strokeLinecap="round" />
    <path d="M6 6.6h.01M8.4 6.6h.01M10.8 6.6h.01" strokeLinecap="round" strokeWidth="2.2" />
  </svg>
);

const MicrophoneIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden>
    <path
      d="M12 15.25a3.75 3.75 0 003.75-3.75V6.75a3.75 3.75 0 10-7.5 0v4.75A3.75 3.75 0 0012 15.25z"
      fill="currentColor"
    />
    <path
      d="M5.75 11.25a.75.75 0 00-1.5 0 7.76 7.76 0 007 7.71v2.29a.75.75 0 001.5 0v-2.29a7.76 7.76 0 007-7.71.75.75 0 00-1.5 0 6.25 6.25 0 11-12.5 0z"
      fill="currentColor"
    />
  </svg>
);

// Localized price tag over the hero mockup. The "$45" used to be baked into
// hero-split.jpg; it now renders as SVG over hero-split-clean.jpg (produced
// by store-metadata/screenshots/clean-hero-split.mjs) so each language shows
// a local amount (landing.hero.priceTag). The viewBox matches the image
// pixel grid (800x1200) and the geometry was pixel-diff fitted against the
// original artwork (Figtree 700 @54.5, white rounded rect y 34 h 84 rx 22
// bleeding off the right edge, text baseline y 94, dark-purple #4C1C7C,
// 27px left padding). The pill grows leftward for longer localized amounts.
const HeroPriceTag: React.FC = () => {
  const { t } = useTranslation();
  const textRef = React.useRef<SVGTextElement>(null);
  // Advance width of "$45" in Figtree 700 at font-size 54.5 — the
  // SSR/first-paint fallback, corrected by measurement once mounted and
  // again when the webfont finishes loading (fallback metrics differ).
  const [textWidth, setTextWidth] = React.useState(97);
  const price = t('landing.hero.priceTag');
  // useEffect (not useLayoutEffect): renderToString in scripts/prerender.mjs
  // warns on layout effects, and the fallback width above keeps the
  // pre-hydration frame correct for the default locale anyway.
  React.useEffect(() => {
    const measure = () => {
      if (textRef.current) setTextWidth(textRef.current.getComputedTextLength());
    };
    measure();
    document.fonts?.ready.then(measure);
  }, [price]);
  const pillX = 770 - textWidth - 27;
  return (
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full"
      viewBox="0 0 800 1200"
      // slice = object-cover: the frame's p-2 padding makes the content box
      // slightly off 2:3, and the overlay must crop exactly like the <img>
      // does so the tag stays glued to the same backdrop pixels.
      preserveAspectRatio="xMidYMid slice"
      aria-hidden
    >
      <rect x={pillX} y={34} width={820 - pillX} height={84} rx={22} fill="#FEFEFE" />
      <text
        ref={textRef}
        x={770}
        y={94}
        textAnchor="end"
        fontFamily="Figtree, Inter, sans-serif"
        fontSize={54.5}
        fontWeight={700}
        fill="#4C1C7C"
      >
        {price}
      </text>
    </svg>
  );
};

// ?web=1 is the escape hatch that keeps
// infra/cloudfront/ios-app-redirect.js from bouncing iPhone visitors straight
// back to this page, and a no-op for everyone else. Module scope so the
// header button and the two hero entry points below share one href.
const WEB_APP_HREF = `${
  (import.meta.env.VITE_WEB_APP_URL as string | undefined) ?? 'https://app.peckle.ai'
}/?web=1`;

// Web is the fastest way in on desktop (no install, and the web app has a real
// wide form factor now), so it leads the store buttons there. Phones keep the
// low-key link below instead: iPhone Safari's first paint is slow enough that
// the CDN bounces those visitors off the web app in the first place.
const WebAppCta: React.FC = () => {
  const { t } = useTranslation();
  // Store-button geometry, brand fill for the hierarchy. Stays brand-600 in
  // dark mode: white on #7a45f5 is ~4.6:1, brand-500 is not.
  return (
    <a
      href={WEB_APP_HREF}
      className="hidden min-h-12 items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-brand-600 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-brand-600/25 transition duration-200 hover:-translate-y-0.5 hover:bg-brand-700 hover:shadow-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 md:inline-flex"
    >
      <BrowserIcon className="h-5 w-5" />
      {t('landing.hero.openWebApp')}
    </a>
  );
};

// Phone-only counterpart of WebAppCta. Both are static (no per-visitor
// checks), so the breakpoint alone decides which one shows and the markup
// stays valid in the prerendered HTML.
const ContinueOnWebLink: React.FC = () => {
  const { t } = useTranslation();
  return (
    <a
      href={WEB_APP_HREF}
      className="mt-2 text-sm font-semibold text-brand-700 underline underline-offset-4 hover:text-brand-800 md:hidden dark:text-brand-300 dark:hover:text-brand-200"
    >
      {t('landing.hero.continueOnWeb')}
    </a>
  );
};

const StoreButtons: React.FC = () => {
  const { t } = useTranslation();
  const appStoreUrl = import.meta.env.VITE_APP_STORE_URL;
  const playStoreUrl = import.meta.env.VITE_PLAY_STORE_URL;

  const base =
    'inline-flex min-h-12 items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-zinc-950 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-zinc-950/10 transition duration-200 hover:-translate-y-0.5 hover:shadow-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 dark:bg-white dark:text-zinc-950';

  // Three peers, one strip: the platforms are alternatives, not a ranked list,
  // so from md they sit in equal-width grid cells instead of pills of ragged
  // width. Three across needs ~513px and the hero column only clears that at
  // xl, so md and lg get a two-across grid. Phones keep the plain centered
  // wrap, where the web button is hidden and only the two stores show.
  return (
    <div className="flex flex-wrap items-center justify-center gap-3 md:grid md:max-w-sm md:grid-cols-2 xl:max-w-xl xl:grid-cols-3">
      <WebAppCta />
      <a href={appStoreUrl} target="_blank" rel="noopener noreferrer" className={base} aria-label={t('landing.hero.appStore')}>
        <AppleIcon className="h-5 w-5" />
        App Store
      </a>
      <a href={playStoreUrl} target="_blank" rel="noopener noreferrer" className={base} aria-label={t('landing.hero.googlePlay')}>
        <PlayIcon className="h-5 w-5" />
        Google Play
      </a>
    </div>
  );
};

// Desktop-only (md+): scans route through OneLink (pid=website_qr) for
// attribution. Centered under the phone mockup, which is where the "get it on
// your phone" message belongs and where the hero has spare vertical space.
// relative: the section's blurred glow is absolutely positioned, so static
// siblings would paint underneath it.
const StoreQrCode: React.FC = () => {
  const { t } = useTranslation();
  // store-qr.svg is generated (store-metadata/ads/generate-qr.mjs) as
  // transparent single-alpha artwork, applied here as a CSS mask so the ink
  // colour comes from the themed background: brand purple on light, white on
  // dark (phone cameras decode inverted codes fine). A <div> instead of <img>
  // because mask-image only keys off the SVG's alpha, not its baked-in fill.
  const maskStyle: React.CSSProperties = {
    WebkitMaskImage: 'url(/store-qr.svg)',
    maskImage: 'url(/store-qr.svg)',
    WebkitMaskSize: '100% 100%',
    maskSize: '100% 100%',
  };
  return (
    <div
      role="img"
      aria-label={t('landing.hero.scanQr')}
      className="hidden h-20 w-20 shrink-0 bg-[#5B2EBF] md:block dark:bg-white short:h-16 short:w-16"
      style={maskStyle}
    />
  );
};

// Always-visible way into the web app, desktop only for the same reason as
// WebAppCta. Matches the h-10 rounded-full geometry of the controls beside it
// so the header stays one coherent row.
const HeaderWebAppButton: React.FC = () => {
  const { t } = useTranslation();
  return (
    <a
      href={WEB_APP_HREF}
      className="mr-1 hidden h-10 items-center gap-2 rounded-full bg-brand-600 px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 md:inline-flex"
    >
      <BrowserIcon className="h-4 w-4" />
      {t('landing.hero.openWebApp')}
    </a>
  );
};

const LanguageSelector: React.FC = () => {
  const { i18n } = useTranslation();
  // resolvedLanguage collapses variants like pt-BR to our resource keys.
  const current = (i18n.resolvedLanguage ?? 'en').split('-')[0];
  const currentLabel =
    supportedLanguages.find((l) => l.code === current)?.label ?? 'English';

  // A transparent native <select> stretched over the styled control: free
  // keyboard/screen-reader/mobile support with the visual we want.
  return (
    <label className="relative flex h-10 cursor-pointer items-center gap-1.5 rounded-full border border-zinc-900/5 bg-white/60 px-3 text-zinc-600 shadow-sm backdrop-blur transition-colors hover:bg-white dark:border-white/10 dark:bg-white/5 dark:text-zinc-300 dark:hover:bg-white/10">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5 shrink-0" aria-hidden>
        <circle cx="12" cy="12" r="9" />
        <path d="M3.6 9h16.8M3.6 15h16.8M12 3a15.4 15.4 0 010 18M12 3a15.4 15.4 0 000 18" />
      </svg>
      <span className="hidden text-sm font-semibold sm:block">{currentLabel}</span>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="hidden h-3.5 w-3.5 sm:block" aria-hidden>
        <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <select
        value={current}
        onChange={(e) => i18n.changeLanguage(e.target.value)}
        aria-label="Language"
        className="absolute inset-0 h-full w-full cursor-pointer appearance-none opacity-0"
      >
        {supportedLanguages.map(({ code, label }) => (
          <option key={code} value={code}>
            {label}
          </option>
        ))}
      </select>
    </label>
  );
};

const ThemeToggleButton: React.FC = () => {
  const { actualMode, toggleMode } = useTheme();
  return (
    <button
      type="button"
      onClick={toggleMode}
      aria-label="Toggle theme"
      className="flex h-10 w-10 items-center justify-center rounded-full border border-zinc-900/5 bg-white/60 text-zinc-600 shadow-sm backdrop-blur transition-colors hover:bg-white dark:border-white/10 dark:bg-white/5 dark:text-zinc-300 dark:hover:bg-white/10"
    >
      {actualMode === 'dark' ? (
        <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5" aria-hidden>
          <path d="M12 7a5 5 0 100 10 5 5 0 000-10zm0-5a1 1 0 011 1v2a1 1 0 11-2 0V3a1 1 0 011-1zm0 18a1 1 0 011 1v2a1 1 0 11-2 0v-2a1 1 0 011-1zM3 11a1 1 0 100 2h2a1 1 0 100-2H3zm16 0a1 1 0 100 2h2a1 1 0 100-2h-2zM5.64 4.22a1 1 0 00-1.42 1.42l1.42 1.41a1 1 0 001.41-1.41L5.64 4.22zm12.72 12.72a1 1 0 00-1.41 1.41l1.41 1.42a1 1 0 001.42-1.42l-1.42-1.41zM4.22 18.36a1 1 0 001.42 1.42l1.41-1.42a1 1 0 10-1.41-1.41l-1.42 1.41zM16.95 5.64a1 1 0 001.41 1.41l1.42-1.41a1 1 0 10-1.42-1.42l-1.41 1.42z" />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5" aria-hidden>
          <path d="M21.75 15.5A9.75 9.75 0 018.5 2.25a.75.75 0 00-.9-.98 10.5 10.5 0 1015.13 15.13.75.75 0 00-.98-.9z" />
        </svg>
      )}
    </button>
  );
};

const LandingPage: React.FC = () => {
  const { t } = useTranslation();
  const { actualMode } = useTheme();
  const year = new Date().getFullYear();

  return (
    // overflow-hidden (not overflow-x-hidden): one-axis hidden turns the other
    // axis into `auto`, so the -bottom-52 glow would add scrollable blank space
    // below the footer.
    <div className="relative flex min-h-dvh flex-col overflow-hidden bg-white font-sans text-zinc-950 dark:bg-zinc-950 dark:text-zinc-50">
      {/* Background glows */}
      <div aria-hidden className="pointer-events-none absolute -left-40 -top-44 h-[34rem] w-[34rem] rounded-full bg-brand-300/35 blur-3xl dark:bg-brand-700/20" />
      <div aria-hidden className="pointer-events-none absolute -bottom-52 -right-36 h-[36rem] w-[36rem] rounded-full bg-brand-300/30 blur-3xl dark:bg-brand-800/25" />
      <div aria-hidden className="pointer-events-none absolute left-1/2 top-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-brand-100/40 blur-3xl dark:bg-brand-950/20" />

      {/* Header */}
      <header className="relative z-10 mx-auto flex w-full max-w-7xl items-center justify-between px-5 py-3 sm:px-8 md:py-4 lg:px-12 short:py-2">
        <div className="flex items-center gap-3">
          <img
            src={actualMode === 'dark' ? '/logo_dark.svg' : '/logo.svg'}
            alt=""
            className="h-9 w-9"
          />
          <div className="text-lg font-extrabold tracking-tight">Peckle</div>
        </div>
        <div className="flex items-center gap-2">
          <HeaderWebAppButton />
          <LanguageSelector />
          <ThemeToggleButton />
        </div>
      </header>

      {/* Hero */}
      {/* content-center packs the grid rows: without it, flex-1 stretch hands
          spare viewport height to the rows themselves, opening a random gap
          between the text and the mockup on tall phones. */}
      <main className="relative z-10 mx-auto grid w-full max-w-7xl flex-1 grid-cols-1 content-center items-center gap-6 px-5 py-4 sm:px-8 md:grid-cols-[minmax(0,1.05fr)_minmax(300px,0.95fr)] md:gap-12 md:py-4 lg:gap-20 lg:px-12 short:py-2">
        <section className="flex flex-col items-center text-center md:items-start md:text-left">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-brand-500/20 bg-brand-50/80 px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.16em] text-brand-800 shadow-sm backdrop-blur dark:border-brand-400/20 dark:bg-brand-400/10 dark:text-brand-200 md:mb-4 short:mb-2">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-600 shadow-[0_0_0_4px_rgba(122,69,245,0.12)] dark:bg-brand-300" />
            {t('landing.category')}
          </div>
          <h1 className="max-w-3xl text-balance text-[1.9rem] font-black leading-[1.06] tracking-[-0.045em] sm:text-5xl lg:text-[3.4rem] lg:leading-[1.04] short:text-4xl short:leading-[1.05]">
            {t('landing.hero.headline')}
          </h1>
          <p className="mt-3 max-w-2xl text-pretty text-[15px] leading-6 text-zinc-600 dark:text-zinc-300 md:mt-4 lg:text-lg lg:leading-8 short:mt-2 short:text-[15px] short:leading-6">
            {t('landing.hero.subheadline')}
          </p>
          <div className="mt-4 md:mt-5 lg:mt-6 short:mt-3">
            <StoreButtons />
          </div>
          <p className="mt-2.5 text-sm font-medium text-zinc-500 dark:text-zinc-400 md:mt-3 short:mt-2">
            {t('landing.hero.supporting')}
            {/* lg, not md: the combined line does not fit the hero column at
                768px. Separate key rather than splitting a translated string. */}
            <span className="hidden lg:inline"> · {t('landing.hero.worksInBrowser')}</span>
          </p>
          <ContinueOnWebLink />
          {/* Keyword coverage (background remover, flyers/banners, WhatsApp
              Status, sellers) lives in index.html meta description + JSON-LD,
              not in visible copy. */}
        </section>

        {/* Before/after phone mockup, with the store QR card beneath it */}
        <section className="relative mx-auto flex w-full max-w-[28rem] flex-col items-center justify-center gap-4 pb-0 pt-11 md:h-full md:py-4 md:pt-4 short:gap-3">
          <div aria-hidden className="absolute inset-x-0 top-1/4 h-1/2 rounded-full bg-brand-500/25 blur-3xl" />

          <div className="absolute left-0 top-0 z-20 flex max-w-[16rem] items-center gap-2.5 rounded-2xl border border-white/80 bg-white/95 px-3.5 py-2.5 text-left shadow-xl shadow-zinc-900/10 backdrop-blur-md dark:border-white/10 dark:bg-zinc-900/95 dark:shadow-black/30 sm:-left-6 md:left-0 lg:-left-10 lg:top-6">
            <span className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-700 dark:bg-brand-900 dark:text-brand-200">
              <span className="absolute inset-0 animate-ping rounded-full bg-brand-400/20 motion-reduce:hidden" />
              <MicrophoneIcon className="relative h-4 w-4" />
            </span>
            <span className="text-[13px] font-semibold leading-snug text-zinc-800 dark:text-zinc-100">
              “{t('landing.hero.voicePrompt')}”
            </span>
          </div>

          <div className="relative aspect-[2/3] w-72 max-w-[calc(100vw-2.5rem)] rounded-[2.2rem] bg-gradient-to-b from-brand-200 via-brand-400 to-brand-600 p-2 shadow-2xl shadow-brand-900/25 dark:from-brand-600 dark:via-brand-800 dark:to-brand-950 sm:w-80 md:w-[min(82%,46dvh)] lg:w-[min(76%,48dvh)] short:w-[min(70%,40dvh)]">
            <div className="relative h-full w-full overflow-hidden rounded-[1.75rem] bg-white ring-1 ring-black/5 dark:bg-zinc-900">
              {/* The image and its container are both 2:3, so object-cover
                  never crops and the SVG price tag overlays it 1:1. */}
              <img
                src="/mockups/hero-split-clean.jpg"
                alt={t('landing.hero.mockAlt')}
                className="h-full w-full object-cover"
                loading="eager"
                decoding="async"
              />
              <HeroPriceTag />
              <span className="absolute bottom-3 left-[25%] -translate-x-1/2 rounded-full bg-white px-2.5 py-1 text-[11px] font-bold text-zinc-900 shadow-md md:bottom-4 md:px-3 md:text-xs">
                {t('landing.hero.beforeLabel')}
              </span>
              <span className="absolute bottom-3 left-[75%] -translate-x-1/2 rounded-full bg-white px-2.5 py-1 text-[11px] font-bold text-brand-700 shadow-md md:bottom-4 md:px-3 md:text-xs">
                {t('landing.hero.afterLabel')}
              </span>
            </div>
          </div>

          <StoreQrCode />
        </section>
      </main>

      {/* Footer */}
      <footer className="relative z-10 mx-auto flex w-full max-w-7xl flex-col items-center justify-between gap-1 px-5 pb-3 pt-1.5 text-center text-xs text-zinc-500 dark:text-zinc-500 sm:flex-row sm:px-8 md:pb-4 md:pt-2 lg:px-12 short:pb-2 short:pt-1">
        <span>{t('footer.copyright', { year })}</span>
        <span className="flex items-center gap-4">
          <a href="/about" className="hover:text-zinc-900 dark:hover:text-zinc-200">
            {t('footer.about', 'About')}
          </a>
          <a href="/terms" className="hover:text-zinc-900 dark:hover:text-zinc-200">
            {t('footer.terms')}
          </a>
          <a href="/privacy" className="hover:text-zinc-900 dark:hover:text-zinc-200">
            {t('footer.privacy')}
          </a>
        </span>
      </footer>
    </div>
  );
};

export default LandingPage;
